import { pb } from '../helpers/pocketbase_helper';
import { createModuleLogger } from '../utils/logger';

const logger = createModuleLogger('CSVProcessorService');

// Constants for validation
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const PHONE_DIGITS = 10;
const SUPPORTED_TABLES = ['cliente'];

// Valid boolean values for activo field
const VALID_BOOLEAN_VALUES = {
  true: ['true', '1', 'yes', 'sí', 'si'],
  false: ['false', '0', 'no']
};

/**
 * Parse CSV content into array of objects
 * @param {string} csvContent - Raw CSV content
 * @returns {Array} Array of parsed objects
 */
export const parseCSV = (csvContent) => {
  if (!csvContent || csvContent.trim() === '') {
    return [];
  }

  const lines = csvContent.trim().split('\n');
  const headers = lines[0].split(',').map(header => header.trim());
  
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line === '') continue; // Skip empty lines
    
    const values = line.split(',').map(value => value.trim());
    const row = {};
    
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    data.push(row);
  }
  
  return data;
};

/**
 * Validate a single row against cliente schema
 * @param {Object} row - Row data to validate
 * @param {number} rowIndex - Row index for error reporting
 * @returns {Object} Validation result with isValid and errors
 */
export const validateRow = (row, rowIndex) => {
  const errors = [];
  
  // Required field validation
  if (!row.razon_social || row.razon_social.trim() === '') {
    errors.push('razon_social es requerido');
  }
  
  // Phone number validation
  if (row.telefono && row.telefono.trim() !== '') {
    const phoneDigits = row.telefono.replace(/\D/g, '');
    if (phoneDigits.length !== PHONE_DIGITS) {
      errors.push('telefono debe tener 10 dígitos');
    }
  }
  
  // Mobile number validation
  if (row.celular && row.celular.trim() !== '') {
    const mobileDigits = row.celular.replace(/\D/g, '');
    if (mobileDigits.length !== PHONE_DIGITS) {
      errors.push('celular debe tener 10 dígitos');
    }
  }
  
  // Boolean validation for activo
  if (row.activo && row.activo.trim() !== '') {
    const activoValue = row.activo.toLowerCase().trim();
    const isValidBoolean = Object.values(VALID_BOOLEAN_VALUES)
      .flat()
      .includes(activoValue);
    
    if (!isValidBoolean) {
      errors.push('activo debe ser true, false, 1, 0, yes, no, sí, no');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors.map(error => `Fila ${rowIndex + 1}: ${error}`)
  };
};

/**
 * Transform CSV string data to appropriate types
 * @param {Array} data - Array of parsed CSV objects
 * @returns {Array} Array of transformed objects
 */
export const transformData = (data) => {
  return data.map(row => {
    const transformed = { ...row };
    
    // Transform phone numbers to numbers
    if (transformed.telefono && transformed.telefono.trim() !== '') {
      transformed.telefono = parseInt(transformed.telefono.replace(/\D/g, ''), 10);
    } else {
      transformed.telefono = null;
    }
    
    if (transformed.celular && transformed.celular.trim() !== '') {
      transformed.celular = parseInt(transformed.celular.replace(/\D/g, ''), 10);
    } else {
      transformed.celular = null;
    }
    
    // Transform boolean activo field
    if (transformed.activo && transformed.activo.trim() !== '') {
      const activoValue = transformed.activo.toLowerCase().trim();
      transformed.activo = VALID_BOOLEAN_VALUES.true.includes(activoValue);
    } else {
      transformed.activo = true; // Default value
    }
    
    return transformed;
  });
};


/**
 * Process bulk upload of CSV data
 * @param {string} csvContent - Raw CSV content
 * @param {string} tableType - Type of table to import to
 * @returns {Object} Processing result
 */
export const processBulkUpload = async (csvContent, tableType) => {
  try {
    // Validate table type
    if (!SUPPORTED_TABLES.includes(tableType)) {
      return {
        success: false,
        error: `Tipo de tabla no soportado: ${tableType}`
      };
    }
    
    // Parse CSV
    const parsedData = parseCSV(csvContent);
    
    if (parsedData.length === 0) {
      return {
        success: false,
        error: 'El archivo CSV está vacío o no contiene datos válidos'
      };
    }
    
    // Validate each row
    const validationResults = parsedData.map((row, index) => 
      validateRow(row, index)
    );
    
    const validRows = [];
    const errors = [];
    
    validationResults.forEach((result, index) => {
      if (result.isValid) {
        validRows.push(parsedData[index]);
      } else {
        errors.push({
          row: index + 1,
          errors: result.errors
        });
      }
    });
    
    // Skip duplicate validation - PocketBase handles duplicates internally
    
    // Transform data
    const transformedData = transformData(validRows);
    
    // Insert data into PocketBase if we have valid rows
    let insertedCount = 0;
    let insertErrors = [];
    
    if (transformedData.length > 0) {
      for (let i = 0; i < transformedData.length; i++) {
        try {
          const record = await pb.collection('cliente').create(transformedData[i]);
          insertedCount++;
        } catch (insertError) {
          console.error(`Error inserting record ${i + 1}:`, insertError);
          insertErrors.push({
            row: i + 1,
            errors: [`Error al insertar registro: ${insertError.message}`]
          });
        }
      }
    }
    
    // Combine validation errors with insertion errors
    const allErrors = [...errors, ...insertErrors];
    
    const result = {
      success: allErrors.length === 0,
      processedCount: insertedCount,
      errorCount: allErrors.length,
      errors: allErrors,
      data: transformedData
    };
    
    return result;
    
  } catch (error) {
    logger.error('Error processing bulk upload:', error);
    return {
      success: false,
      error: `Error al procesar la carga masiva: ${error.message}`
    };
  }
};

/**
 * Validate uploaded file
 * @param {File} file - Uploaded file
 * @returns {Object} Validation result
 */
export const validateFile = (file) => {
  const errors = [];
  
  if (!file) {
    errors.push('No se seleccionó ningún archivo');
    return { isValid: false, errors };
  }
  
  if (file.size > MAX_FILE_SIZE) {
    errors.push(`El archivo es demasiado grande. Tamaño máximo: ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
  }
  
  if (!file.name.toLowerCase().endsWith('.csv')) {
    errors.push('El archivo debe ser un archivo CSV');
  }
  
  if (file.type && !file.type.includes('text/csv') && !file.type.includes('application/csv')) {
    errors.push('Tipo de archivo no válido. Debe ser un archivo CSV');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
